import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSessionContext } from '../adapters/AdapterProvider'

type Theme = 'light' | 'dark'

/**
 * Derives a 1-2 char avatar label from a display name: first+last initial
 * for multi-word names, first two chars for a single token. Returns null
 * for empty/whitespace-only input so callers can fall back to a guest glyph.
 */
export function initialsFromName(name: string): string | null {
  const tokens = name.split(/\s+/).filter(Boolean)
  if (tokens.length === 0) return null
  if (tokens.length === 1) return tokens[0]!.slice(0, 2).toUpperCase()
  return `${tokens[0]![0]}${tokens[tokens.length - 1]![0]}`.toUpperCase()
}

function applyTheme(theme: Theme) {
  const root = document.documentElement
  if (theme === 'dark') root.classList.add('dark')
  else root.classList.remove('dark')
}

function readStoredTheme(): Theme {
  const stored = localStorage.getItem('label-designer.theme')
  if (stored === 'dark' || stored === 'light') return stored
  const mql = typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null
  return mql?.matches ? 'dark' : 'light'
}

export function UserMenu() {
  const { t } = useTranslation()
  const [theme, setTheme] = useState<Theme>('light')
  const sessionContext = useSessionContext()
  const userName = sessionContext?.user?.name?.trim() || undefined
  const initials = userName ? initialsFromName(userName) : null

  useEffect(() => {
    const initial = readStoredTheme()
    setTheme(initial)
    applyTheme(initial)
  }, [])

  const toggle = () => {
    const next: Theme = theme === 'dark' ? 'light' : 'dark'
    setTheme(next)
    applyTheme(next)
    localStorage.setItem('label-designer.theme', next)
  }

  const targetLabel = theme === 'dark' ? t('userMenu.light') : t('userMenu.dark')

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={toggle}
        aria-label={t('userMenu.toggleTo', { mode: targetLabel.toLowerCase() })}
        className="rounded-md border border-stone-300 px-2 py-1 text-xs text-stone-700 hover:bg-stone-100 dark:border-stone-700 dark:text-stone-300 dark:hover:bg-stone-800"
      >
        {targetLabel}
      </button>
      {initials ? (
        <div
          title={userName}
          aria-label={t('userMenu.avatarLabel', { name: userName })}
          className="flex h-8 w-8 items-center justify-center rounded-full bg-stone-200 text-sm font-medium text-stone-700 dark:bg-stone-800 dark:text-stone-300"
        >
          {initials}
        </div>
      ) : (
        <div
          data-testid="user-avatar-guest"
          title={t('userMenu.guestLabel')}
          aria-label={t('userMenu.guestLabel')}
          className="flex h-8 w-8 items-center justify-center rounded-full bg-stone-200 text-stone-500 dark:bg-stone-800 dark:text-stone-400"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="h-4 w-4">
            <path d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4Zm0 2c-3.33 0-8 1.67-8 5v1h16v-1c0-3.33-4.67-5-8-5Z" />
          </svg>
        </div>
      )}
    </div>
  )
}
